var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Badge, Button, Page, Table } from '@dixa/lib-shared-habla';
import { useCallback, useState } from 'react';
import { analytics } from 'app/utils/segment';
import { useDixaMessengerApi } from 'components/settings/components/sections/dixaMessenger/api/apiProvider';
import { IfAgentHasRole } from 'components/shared/agentRole';
import { ConfirmModal } from 'components/shared/confirmModal/components/confirmModal';
import { useMessengers } from 'hooks/useMessengers';
import { useNotifications } from 'hooks/useNotifications';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { AddModal } from './components/addModal/addModal';
import { ListingBody } from './components/body/listingBody';
import { ListingFoot } from './components/foot/listingFoot';
import { RenameModal } from './components/renameModal/renameModal';
export const Listing = () => {
    const notify = useNotifications();
    const navigate = useAppNavigate();
    const messengerApi = useDixaMessengerApi();
    const { items, asyncStatus, hasNextPage, hasPreviousPage, nextPage, previousPage, refreshItems, total = items.length, } = useMessengers({ limit: 125 });
    const [activeModalName, setActiveModalName] = useState(null);
    const [modalMessengerId, setModalMessengerId] = useState(null);
    const showSpecificModal = (messengerId, modal) => {
        analytics.messengerFormOpened({ mode: modal });
        setActiveModalName(modal);
        setModalMessengerId(messengerId);
    };
    const showAddModal = useCallback(() => {
        showSpecificModal(null, 'add');
    }, []);
    const showRenameModal = useCallback((messengerId) => {
        analytics.messengerOptionsMenuOptionClicked({ option: 'copy-embed' });
        showSpecificModal(messengerId, 'rename');
    }, []);
    const showDeleteModal = useCallback((messengerId) => {
        analytics.messengerOptionsMenuOptionClicked({ option: 'delete' });
        showSpecificModal(messengerId, 'delete');
    }, []);
    const goToFlow = useCallback((flowId) => {
        analytics.messengerOptionsMenuOptionClicked({ option: 'edit-flow' });
        navigate({
            path: '/settings/dixa-messenger/flow/:flowId',
            params: { path: { flowId } },
        });
    }, [navigate]);
    const goToAudit = useCallback((flowId) => {
        analytics.messengerFlowOptionsMenuOptionAuditLogClicked();
        navigate({
            path: '/settings/dixa-messenger/flow/:flowId/history',
            params: { path: { flowId } },
        });
    }, [navigate]);
    const goToAppearance = useCallback((messengerId) => {
        analytics.messengerOptionsMenuOptionClicked({
            option: 'edit-appearance',
        });
        navigate({
            path: '/settings/dixa-messenger/appearance/:messengerId',
            params: { path: { messengerId } },
        });
    }, [navigate]);
    const closeModal = useCallback(() => {
        if (activeModalName !== null) {
            analytics.messengerFormCancelled({ mode: activeModalName });
        }
        setActiveModalName(null);
        setModalMessengerId(null);
    }, [activeModalName]);
    const messenger = items.find((messenger) => messenger.id === modalMessengerId) || null;
    const duplicateMessengerActionCallback = useCallback((messengerId) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            analytics.messengerOptionsMenuOptionClicked({ option: 'duplicate' });
            yield messengerApi.duplicate(messengerId);
            refreshItems();
            notify({
                title: 'Messenger duplicated',
                text: 'Messenger was successfully copied',
                icon: 'done',
                className: 'success',
                time: 5000,
            });
        }
        catch (_a) {
            notify({
                title: 'An error occurred',
                text: `An error occurred when trying to duplicate the messenger.`,
                icon: 'exclamation',
                className: 'danger',
            });
        }
    }), [notify, refreshItems, messengerApi]);
    const deleteMessengerModalCallback = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (!modalMessengerId)
                return;
            analytics.messengerFormSubmitted({ mode: 'delete' });
            yield messengerApi.delete(modalMessengerId);
            notify({
                title: 'Messenger deleted',
                text: `Messenger was deleted`,
                icon: 'done',
                className: 'success',
                time: 5000,
            });
            closeModal();
            refreshItems();
        }
        catch (_b) {
            notify({
                title: 'An error occurred',
                text: `An error occurred when trying to delete the messenger.`,
                icon: 'exclamation',
                className: 'danger',
            });
        }
    }), [closeModal, modalMessengerId, notify, refreshItems, messengerApi]);
    const handleNewMessengerAdded = useCallback(({ id }) => setTimeout(() => {
        navigate({
            path: '/settings/dixa-messenger/appearance/:messengerId',
            params: { path: { messengerId: id } },
        });
    }), [navigate]);
    return (_jsxs(_Fragment, { children: [_jsx(Page.Section, Object.assign({ title: "All messengers", titleMeta: total && (_jsx(Badge, Object.assign({ size: "small", variant: "secondary" }, { children: total }))), controls: _jsx(IfAgentHasRole, Object.assign({ role: 'admin' }, { children: _jsx(Button, Object.assign({ onClick: showAddModal, id: "add-messenger-btn" }, { children: "Add messenger" })) })) }, { children: _jsxs(Table, Object.assign({ framed: true, hoverable: true, stickyHeader: true, stickyFooter: true, headerWithBackground: true }, { children: [_jsx(Table.Thead, { children: _jsxs(Table.Tr, { children: [_jsx(Table.Th, Object.assign({ columnId: "name" }, { children: "Name" })), _jsx(Table.Th, { columnId: null })] }) }), _jsx(Table.Tbody, { children: _jsx(ListingBody, { asyncStatus: asyncStatus, items: items, onRefreshItems: refreshItems, onRename: showRenameModal, onDelete: showDeleteModal, onDuplicateMessenger: duplicateMessengerActionCallback, onEditSettings: goToAppearance, onEditFlow: goToFlow, onAudit: goToAudit }) }), _jsx(ListingFoot, { hasNextPage: hasNextPage, hasPreviousPage: hasPreviousPage, nextPage: nextPage, previousPage: previousPage, asyncStatus: asyncStatus })] })) })), _jsx(AddModal, { show: activeModalName === 'add', onClose: closeModal, onAdd: handleNewMessengerAdded }), _jsx(RenameModal, { show: activeModalName === 'rename' && modalMessengerId !== null, messengerId: modalMessengerId, initialValues: { name: (messenger === null || messenger === void 0 ? void 0 : messenger.name) || '' }, closeModal: closeModal, refreshItems: refreshItems }), _jsx(ConfirmModal, { cancel: closeModal, cancelText: "Cancel", confirm: deleteMessengerModalCallback, confirmText: "Yes, delete it now", headline: "Are you sure?", isShown: activeModalName === 'delete' && modalMessengerId !== null, text: `Are you sure you want to delete the messenger
            "${messenger === null || messenger === void 0 ? void 0 : messenger.name}"?` })] }));
};
